import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import FilterBar from "@/components/FilterBar";
import { useAuth } from "@/context/AuthContext";
import { money, money2, num, fiscalMonthLabel, TIER_LABELS, TIER_COLORS } from "@/lib/format";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Info, History, CalendarClock, Lock, LockOpen, FlaskConical, Plus, Trash2, ChevronRight, Users2, RotateCcw } from "lucide-react";
import ScenarioLab from "@/components/ScenarioLab";

function buildParams(f, applyDebtor, fy) {
  const p = { apply_debtor_rule: applyDebtor };
  if (fy) p.fiscal_year = fy;
  ["department_id", "user_id"].forEach((k) => { if (f[k]) p[k] = f[k]; });
  return p;
}

function Kpi({ label, value, color, testid }) {
  return (
    <div className="border border-border bg-card p-5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`font-mono text-2xl mt-1 ${color || ""}`}>{value}</div>
    </div>
  );
}

function MonthCell({ basic, nett, noncomm, recovery, debtor, locked, onSetBasic, onSetBanking, basicTestid, nettTestid, noncommTestid, recoveryTestid, debtorTestid }) {
  const enterBlur = (e) => { if (e.key === "Enter") e.target.blur(); };
  const commitBasic = (e) => { const n = parseFloat(e.target.value || 0) || 0; if (n !== basic) onSetBasic(n); };
  const commitNett = (e) => { const n = parseFloat(e.target.value || 0) || 0; if (n !== nett) onSetBanking(n, noncomm, recovery, debtor); };
  const commitNonComm = (e) => { const n = parseFloat(e.target.value || 0) || 0; if (n !== noncomm) onSetBanking(nett, n, recovery, debtor); };
  const commitRecovery = (e) => { const n = parseFloat(e.target.value || 0) || 0; if (n !== recovery) onSetBanking(nett, noncomm, n, debtor); };
  return (
    <div className="flex flex-col items-end gap-1">
      <input
        key={`b${basic}`} type="number" defaultValue={basic || ""} disabled={locked}
        onBlur={commitBasic} onKeyDown={enterBlur} placeholder="basic" data-testid={basicTestid}
        className="w-28 text-right font-mono text-xs border border-border bg-background px-1.5 py-1 disabled:opacity-60 focus:border-brand outline-none placeholder:text-muted-foreground/50"
      />
      <input
        key={`n${nett}`} type="number" defaultValue={nett || ""} disabled={locked}
        onBlur={commitNett} onKeyDown={enterBlur} placeholder="banking – comm" title="Commissionable banking — counts toward commission" data-testid={nettTestid}
        className="w-28 text-right font-mono text-xs border border-brand/40 bg-secondary/40 px-1.5 py-1 disabled:opacity-60 focus:border-brand outline-none placeholder:text-muted-foreground/50"
      />
      <input
        key={`nc${noncomm}`} type="number" defaultValue={noncomm || ""} disabled={locked}
        onBlur={commitNonComm} onKeyDown={enterBlur} placeholder="banking – non-comm" title="Non-commissionable banking — shown but does NOT count toward commission" data-testid={noncommTestid}
        className="w-28 text-right font-mono text-xs border border-dashed border-border bg-background px-1.5 py-1 disabled:opacity-60 focus:border-brand outline-none placeholder:text-muted-foreground/50"
      />
      <input
        key={`rc${recovery}`} type="number" defaultValue={recovery || ""} disabled={locked}
        onBlur={commitRecovery} onKeyDown={enterBlur} placeholder="recovery / other" title="Recovery / other — counts toward the commission calculation but is NOT added to the banked total" data-testid={recoveryTestid}
        className="w-28 text-right font-mono text-xs border border-positive/40 bg-positive/5 px-1.5 py-1 disabled:opacity-60 focus:border-brand outline-none placeholder:text-muted-foreground/50"
      />
      <label className={`flex items-center gap-1 text-[10px] ${debtor ? "text-destructive" : "text-muted-foreground"} ${locked ? "opacity-60" : ""}`}>
        <input type="checkbox" checked={!!debtor} disabled={locked} onChange={(e) => onSetBanking(nett, noncomm, recovery, e.target.checked)} data-testid={debtorTestid} /> 90+
      </label>
    </div>
  );
}

function zeroReason(r, q, cfg) {
  const titles = (cfg?.eligible_titles || []).join(", ");
  if (!r.eligible) return `Not eligible: title "${r.title}" isn't a commission title (eligible: ${titles}). Change it in Users, or add "${r.title}" in Settings → Commission Rules.`;
  if (q.debtor_blocked) return `Withheld: a month in this quarter is flagged 90+ day debtors.`;
  const minMult = cfg?.min_multiple ?? 1.8;
  if (q.multiple < minMult) return `Below minimum: banked ${num(q.multiple, 2)}× basic (needs ≥ ${minMult}×).`;
  if (q.difference <= 0) return `Below break-even: banked ${money2(q.comm_base ?? q.total_nett)} vs break-even ${money2(q.breakeven)} (${cfg?.breakeven_multiple ?? 2.5}× basic + ${money2(q.jnr_staff || 0)} junior staff). Shortfall carries forward.`;
  return "";
}

function JuniorRows({ fy, userId, midx, locked, canEdit, onReload }) {
  const [juniors, setJuniors] = useState([]);
  const load = useCallback(() => {
    api.get("/commission/juniors", { params: { user_id: userId, fiscal_year: fy } }).then((r) => setJuniors(r.data)).catch(() => {});
  }, [userId, fy]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    try { await api.post("/commission/juniors", { fee_earner_id: userId, fiscal_year: fy, name: "" }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const setAmount = async (jid, fm, val) => {
    try { await api.put(`/commission/juniors/${jid}`, { fiscal_month: fm, amount: parseFloat(val || 0) || 0 }); load(); onReload && onReload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const setName = async (jid, name) => {
    try { await api.put(`/commission/juniors/${jid}`, { name }); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (jid) => {
    try { await api.delete(`/commission/juniors/${jid}`); load(); onReload && onReload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const qTotal = (j) => midx.reduce((a, fm) => a + (j.amounts[String(fm)] || 0), 0);
  const monthTotal = (fm) => juniors.reduce((a, j) => a + (j.amounts[String(fm)] || 0), 0);
  const bg = "bg-secondary/40";

  return (
    <>
      <TableRow className="hover:bg-secondary/40 border-t-2 border-border" data-testid={`junior-panel-${userId}`}>
        <TableCell colSpan={6} className={`${bg} py-2`}>
          <span className="text-[11px] uppercase tracking-wide text-muted-foreground font-semibold flex items-center gap-1.5"><Users2 className="h-3.5 w-3.5" /> Junior staff on these cases — added to break-even</span>
        </TableCell>
        <TableCell colSpan={3} className={`${bg} text-right py-2`}>
          {canEdit && !locked && <Button size="sm" variant="outline" className="rounded-none h-7 gap-1 text-xs" onClick={add} data-testid={`junior-add-${userId}`}><Plus className="h-3 w-3" /> Add</Button>}
        </TableCell>
      </TableRow>
      {juniors.length === 0 && (
        <TableRow className="hover:bg-secondary/40"><TableCell colSpan={9} className={`${bg} text-xs text-muted-foreground italic py-2`}>No junior staff added yet.</TableCell></TableRow>
      )}
      {juniors.map((j) => (
        <TableRow key={j.id} className="hover:bg-secondary/40" data-testid={`junior-row-${j.id}`}>
          <TableCell colSpan={2} className={`${bg} py-1 align-middle`}>
            <input defaultValue={j.name} disabled={locked || !canEdit} placeholder="Junior name" onBlur={(e) => setName(j.id, e.target.value)} className="w-full max-w-[260px] border border-border bg-background px-2 py-1.5 text-xs disabled:opacity-60 outline-none focus:border-brand" data-testid={`junior-name-${j.id}`} />
          </TableCell>
          {midx.map((fm) => (
            <TableCell key={fm} className={`${bg} text-right py-1 align-middle`}>
              <input key={`${fm}-${j.amounts[String(fm)] || 0}`} type="number" defaultValue={j.amounts[String(fm)] || ""} disabled={locked || !canEdit} placeholder="0" onBlur={(e) => setAmount(j.id, fm, e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }} className="w-24 text-right font-mono text-xs border border-border bg-background px-1.5 py-1.5 disabled:opacity-60 outline-none focus:border-brand" data-testid={`junior-amt-${j.id}-${fm}`} />
            </TableCell>
          ))}
          <TableCell className={`${bg} text-right font-mono text-xs font-semibold py-1 align-middle`}>{money2(qTotal(j))}</TableCell>
          <TableCell colSpan={2} className={bg}></TableCell>
          <TableCell className={`${bg} text-right py-1 align-middle`}>{canEdit && !locked && <button onClick={() => remove(j.id)} className="text-muted-foreground hover:text-destructive" data-testid={`junior-del-${j.id}`}><Trash2 className="h-3.5 w-3.5" /></button>}</TableCell>
        </TableRow>
      ))}
      {juniors.length > 0 && (
        <TableRow className="hover:bg-secondary/40">
          <TableCell colSpan={2} className={`${bg} text-xs text-muted-foreground font-semibold py-1.5`}>Total junior staff</TableCell>
          {midx.map((fm) => <TableCell key={fm} className={`${bg} text-right font-mono text-xs font-semibold py-1.5`}>{money2(monthTotal(fm))}</TableCell>)}
          <TableCell className={`${bg} text-right font-mono text-xs font-semibold text-positive py-1.5`}>{money2(midx.reduce((a, fm) => a + monthTotal(fm), 0))}</TableCell>
          <TableCell colSpan={3} className={bg}></TableCell>
        </TableRow>
      )}
    </>
  );
}

function CarryInCell({ q, qIndex, userId, editable, locked, onSetCarry }) {
  if (locked || !editable) return null;
  const commit = (e) => { const n = parseFloat(e.target.value || 0) || 0; if (n !== (q.carry_in || 0)) onSetCarry(userId, qIndex, n); };
  return (
    <div className="mt-1 flex items-center justify-end gap-1" title="Override the opening carry-in — a negative starting balance added to break-even. Auto-carried from the previous quarter; override if needed.">
      <span className="text-[9px] uppercase tracking-wide text-muted-foreground">set carry-in</span>
      <input
        key={`ci-${q.carry_in}-${q.carry_in_overridden}`} type="number" defaultValue={q.carry_in || ""}
        onBlur={commit} onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }} placeholder="0"
        data-testid={`carry-in-${userId}-${qIndex}`}
        className={`w-20 text-right font-mono text-[10px] px-1 py-0.5 outline-none focus:border-brand bg-background border ${q.carry_in_overridden ? "border-warning text-warning" : "border-border"}`}
      />
      {q.carry_in_overridden && <button onClick={() => onSetCarry(userId, qIndex, null)} title="Reset to auto-carried value" data-testid={`carry-reset-${userId}-${qIndex}`} className="text-muted-foreground hover:text-brand"><RotateCcw className="h-3 w-3" /></button>}
    </div>
  );
}

function QuarterTable({ items, qIndex, startMonth, onSetBasic, onSetBanking, locked, canEditRow, cfg, fy, onReload, isAdmin, onSetCarry, onSetRecoveryLabel }) {
  const midx = [(qIndex - 1) * 3 + 1, (qIndex - 1) * 3 + 2, (qIndex - 1) * 3 + 3];
  const labels = midx.map((m) => fiscalMonthLabel(m, startMonth));
  const visible = items;
  const [expanded, setExpanded] = useState(new Set());
  const toggle = (id) => setExpanded((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  return (
    <div className="border border-border bg-card overflow-x-auto" data-testid="commission-quarter-table">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead>Fee earner</TableHead>
            <TableHead>Title</TableHead>
            {labels.map((l) => (
              <TableHead key={l} className="text-right">
                <div>{l}</div>
                <div className="text-[9px] font-normal normal-case text-muted-foreground/70">basic · comm · non-comm · recovery · 90+</div>
              </TableHead>
            ))}
            <TableHead className="text-right font-semibold">Qtr Total</TableHead>
            <TableHead className="text-right">Mult.</TableHead>
            <TableHead>Tier</TableHead>
            <TableHead className="text-right">Commission</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {visible.length === 0 && (
            <TableRow><TableCell colSpan={5 + labels.length} className="text-center py-10 text-muted-foreground">No earners in scope.</TableCell></TableRow>
          )}
          {visible.map((r) => {
            const q = r.q;
            const editable = canEditRow(r.user_id);
            return (
              <React.Fragment key={r.user_id}>
              <TableRow className="hover:bg-secondary/40" data-testid={`cq-row-${r.user_id}`}>
                <TableCell className="font-medium align-top">
                  <button onClick={() => toggle(r.user_id)} className="inline-flex items-center gap-1 hover:text-brand transition-colors" data-testid={`junior-toggle-${r.user_id}`}>
                    <ChevronRight className={`h-3.5 w-3.5 transition-transform ${expanded.has(r.user_id) ? "rotate-90" : ""}`} />
                    {r.name}
                  </button>
                  {!r.eligible && <Badge variant="outline" className="ml-2 rounded-none text-[9px] text-muted-foreground">n/e</Badge>}
                  {!locked && editable && (
                    <input
                      key={`rl-${r.user_id}-${q.recovery_label || ""}`} type="text" defaultValue={q.recovery_label || ""}
                      onBlur={(e) => { const v = e.target.value.trim(); if (v !== (q.recovery_label || "")) onSetRecoveryLabel(r.user_id, qIndex, v); }}
                      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                      placeholder="recovery label" title="Label for the recovery / other commissionable line (applies across the 3 months)"
                      data-testid={`recovery-label-${r.user_id}-${qIndex}`}
                      className="mt-1 block w-32 text-xs border border-positive/40 bg-positive/5 px-1.5 py-1 focus:border-brand outline-none placeholder:text-muted-foreground/50"
                    />
                  )}
                </TableCell>
                <TableCell className="align-top"><Badge variant="outline" className="rounded-none text-[10px]">{r.title}</Badge></TableCell>
                {q.months.map((m) => (
                  <TableCell key={m.month} className="text-right align-top">
                    <MonthCell
                      basic={m.basic}
                      nett={m.nett}
                      noncomm={m.noncomm}
                      recovery={m.recovery}
                      debtor={m.debtor}
                      locked={locked || !editable}
                      onSetBasic={(amt) => onSetBasic(r.user_id, m.month, amt)}
                      onSetBanking={(nett, noncomm, recovery, debtor) => onSetBanking(r.user_id, m.month, nett, noncomm, recovery, debtor)}
                      basicTestid={`basic-${r.user_id}-${m.month}`}
                      nettTestid={`banked-${r.user_id}-${m.month}`}
                      noncommTestid={`noncomm-${r.user_id}-${m.month}`}
                      recoveryTestid={`recovery-${r.user_id}-${m.month}`}
                      debtorTestid={`debtor-${r.user_id}-${m.month}`}
                    />
                  </TableCell>
                ))}
                <TableCell className="text-right border-l border-border align-top">
                  <div className="font-mono font-semibold" title="Banking (comm) this quarter — recovery is shown separately and does not add to this total">{money2(q.total_nett)}</div>
                  {q.noncomm > 0 && <div className="text-[10px] text-muted-foreground font-mono" title="Non-commissionable banking — shown but not counted toward commission">+ non-comm {money2(q.noncomm)}</div>}
                  {q.recovery > 0 && <div className="text-[10px] text-positive font-mono" title="Recovery / other — NOT added to the banked total above, but still counts toward the commission calculation">+ {q.recovery_label || "recovery"} {money2(q.recovery)} <span className="text-muted-foreground">(commissionable)</span></div>}
                  {q.quarterly_basic > 0 && (
                    <div className="mt-1 space-y-0.5 text-[10px] font-mono text-muted-foreground min-w-[150px]" data-testid={`be-breakdown-${r.user_id}-${qIndex}`}>
                      <div className="flex justify-between gap-3"><span>Basic (×3 mo)</span><span>{money2(q.quarterly_basic)}</span></div>
                      <div className="flex justify-between gap-3"><span>Break-even ×{cfg?.breakeven_multiple ?? 2.5}</span><span>{money2(q.breakeven - q.jnr_staff)}</span></div>
                      {q.jnr_staff > 0 && <div className="flex justify-between gap-3"><span>+ Junior fees</span><span>{money2(q.jnr_staff)}</span></div>}
                      {(q.carry_in > 0 || q.carry_in_overridden) && <div className="flex justify-between gap-3 text-warning"><span>+ Carry-in</span><span>{money2(q.carry_in)}</span></div>}
                      <div className="flex justify-between gap-3 border-t border-border pt-0.5 text-foreground font-semibold"><span>= Break-even</span><span data-testid={`be-total-${r.user_id}-${qIndex}`}>{money2(q.breakeven + q.carry_in)}</span></div>
                    </div>
                  )}
                  <CarryInCell q={q} qIndex={qIndex} userId={r.user_id} editable={isAdmin} locked={locked} onSetCarry={onSetCarry} />
                </TableCell>
                <TableCell className="text-right font-mono text-xs align-top">{q.quarterly_basic > 0 ? `${num(q.multiple, 2)}x` : "—"}</TableCell>
                <TableCell className="align-top">{q.quarterly_basic > 0 ? <Badge variant="outline" className={`rounded-none text-[9px] ${TIER_COLORS[q.tier] || ""}`}>{TIER_LABELS[q.tier] || q.tier}</Badge> : <span className="text-muted-foreground text-xs">—</span>}</TableCell>
                <TableCell className="text-right align-top">
                  <div className="font-mono font-semibold text-positive">{money2(q.commission)}{q.capped && <span className="ml-1 text-[9px] text-warning">CAP</span>}{q.debtor_blocked && <span className="ml-1 text-[9px] text-destructive">90+</span>}</div>
                  {q.commission === 0 && (q.comm_base ?? q.total_nett) > 0 && (() => { const why = zeroReason(r, q, cfg); return why ? <div className="mt-1 max-w-[220px] text-[10px] leading-tight text-muted-foreground font-normal text-left ml-auto" data-testid={`zero-reason-${r.user_id}`}>{why}</div> : null; })()}
                </TableCell>
              </TableRow>
              {expanded.has(r.user_id) && (
                <JuniorRows fy={fy} userId={r.user_id} midx={midx} locked={locked} canEdit={editable} onReload={onReload} />
              )}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

function QuarterPanel({ q, fy, lock, items, startMonth, isAdmin, onLock, onUnlock, onSetBasic, onSetBanking, canEditRow, cfg, onReload, onSetCarry, onSetRecoveryLabel }) {
  const t = items.reduce((a, r) => ({ banked: a.banked + (r.q?.total_nett || 0), commission: a.commission + (r.q?.commission || 0), carry: a.carry + (r.q?.carry_out || 0) }), { banked: 0, commission: 0, carry: 0 });
  const monthRange = [(q - 1) * 3 + 1, (q - 1) * 3 + 2, (q - 1) * 3 + 3].map((m) => fiscalMonthLabel(m, startMonth)).join(" · ");
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <Badge variant="outline" className="rounded-none" data-testid={`period-badge-${q}`}>{fy} · Q{q}</Badge>
          <span className="text-xs text-muted-foreground">{monthRange}</span>
          {lock ? (
            <Badge variant="outline" className="rounded-none bg-positive/10 text-positive border-positive/30 gap-1" data-testid={`lock-badge-${q}`}><Lock className="h-3 w-3" /> Locked {new Date(lock.locked_at).toLocaleDateString()}{lock.locked_by_name ? ` · ${lock.locked_by_name}` : ""}</Badge>
          ) : (
            <Badge variant="outline" className="rounded-none text-muted-foreground gap-1"><LockOpen className="h-3 w-3" /> Open</Badge>
          )}
        </div>
        {isAdmin && (lock ? (
          <Button variant="outline" size="sm" className="rounded-none gap-2" onClick={() => onUnlock(q)} data-testid={`unlock-btn-${q}`}><LockOpen className="h-4 w-4" /> Unlock quarter</Button>
        ) : (
          <Button size="sm" className="rounded-none gap-2" onClick={() => onLock(q)} data-testid={`lock-btn-${q}`}><Lock className="h-4 w-4" /> Close &amp; lock quarter</Button>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Kpi testid={`q${q}-banked`} label="Quarter banked" value={money(t.banked)} />
        <Kpi testid={`q${q}-commission`} label={lock ? "Commission (locked)" : "Commission payable"} value={money(t.commission)} color="text-positive" />
        <Kpi testid={`q${q}-carry`} label="Carry-over out" value={money(t.carry)} color="text-warning" />
      </div>
      {lock && <div className="text-xs text-muted-foreground flex items-center gap-1.5 border border-border bg-secondary/40 px-3 py-2"><Lock className="h-3 w-3 shrink-0" /> Snapshot frozen at lock time — banking edits to this quarter are blocked and will not change these figures.</div>}
      {!lock && <div className="text-xs text-muted-foreground">For each person, enter the monthly <b>basic salary</b> and that month&apos;s <b>net banked</b> directly in the boxes below, and flag any month with <b>90+ day debtors</b>. Basic auto-fills forward; banking is captured per month — no separate page needed.</div>}
      <QuarterTable items={items} qIndex={q} startMonth={startMonth} onSetBasic={onSetBasic} onSetBanking={onSetBanking} locked={!!lock} canEditRow={canEditRow} cfg={cfg} fy={fy} onReload={onReload} isAdmin={isAdmin} onSetCarry={onSetCarry} onSetRecoveryLabel={onSetRecoveryLabel} />
    </div>
  );
}

export default function Commission() {
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin";
  const canEditRow = () => user?.role === "super_admin";
  const [filters, setFilters] = useState({});
  const [applyDebtor, setApplyDebtor] = useState(true);
  const [run, setRun] = useState(null);
  const [cur, setCur] = useState(null);
  const [prevQuarter, setPrevQuarter] = useState("1");
  const [locks, setLocks] = useState([]);
  const [snapshots, setSnapshots] = useState({});

  useEffect(() => { api.get("/fiscal/current").then((r) => { setCur(r.data); }); }, []);

  const fy = filters.fiscal_year || cur?.fiscal_year;

  const loadLocks = useCallback(async () => {
    if (!fy) return;
    try { const r = await api.get("/commission/locks", { params: { fiscal_year: fy } }); setLocks(r.data); } catch (_) { /* ignore */ }
  }, [fy]);

  useEffect(() => {
    if (!cur) return;
    api.get("/commission/run", { params: buildParams(filters, applyDebtor, fy) }).then((r) => setRun(r.data)).catch(() => {});
  }, [filters, applyDebtor, cur, fy]);

  useEffect(() => {
    if (!fy) return;
    api.get("/commission/locks", { params: { fiscal_year: fy } }).then((r) => setLocks(r.data)).catch(() => {});
  }, [fy]);

  const cfg = run?.config;
  const rows = run?.rows || [];
  const startMonth = run?.start_month || cur?.start_month || 1;
  const currentQ = cur?.quarter || 1;
  const pq = parseInt(prevQuarter);

  const lockOf = (q) => locks.find((l) => l.fiscal_year === fy && l.quarter === q);

  const fetchSnapshot = useCallback(async (q) => {
    try { const r = await api.get("/commission/snapshot", { params: { fiscal_year: fy, quarter: q } }); setSnapshots((p) => ({ ...p, [`${fy}-${q}`]: r.data })); } catch (_) { /* ignore */ }
  }, [fy]);

  useEffect(() => {
    [currentQ, pq].forEach((q) => {
      const locked = locks.find((l) => l.fiscal_year === fy && l.quarter === q);
      if (locked && !snapshots[`${fy}-${q}`]) fetchSnapshot(q);
    });
  }, [locks, currentQ, pq, fy, fetchSnapshot, snapshots]);

  const itemsFor = (q) => {
    if (lockOf(q)) {
      const snap = snapshots[`${fy}-${q}`];
      return (snap?.rows || []).map((r) => ({ ...r, q: r.quarter }));
    }
    return rows.map((r) => ({ ...r, q: r.quarters[q - 1] })).filter((r) => r.q);
  };

  const lockQuarter = async (q) => {
    try { await api.post("/commission/lock", { fiscal_year: fy, quarter: q }); toast.success(`Locked ${fy} · Q${q}`); await loadLocks(); fetchSnapshot(q); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const unlockQuarter = async (q) => {
    try { await api.post("/commission/unlock", { fiscal_year: fy, quarter: q }); toast.success(`Unlocked ${fy} · Q${q}`); setSnapshots((p) => { const n = { ...p }; delete n[`${fy}-${q}`]; return n; }); await loadLocks(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const reloadRun = useCallback(async () => {
    try { const r = await api.get("/commission/run", { params: buildParams(filters, applyDebtor, fy) }); setRun(r.data); } catch (_) { /* ignore */ }
  }, [filters, applyDebtor, fy]);

  const setBasic = async (userId, fiscalMonth, amount) => {
    try {
      await api.put("/commission/basics", { user_id: userId, fiscal_year: fy, fiscal_month: fiscalMonth, amount });
      await reloadRun();
      toast.success("Basic salary updated");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const setBanking = async (userId, fiscalMonth, nett, noncomm, recovery, debtor) => {
    try {
      await api.put("/commission/banking", { user_id: userId, fiscal_year: fy, fiscal_month: fiscalMonth, nett, nett_noncomm: noncomm, recovery, debtor });
      await reloadRun();
      toast.success("Banking updated");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const setRecoveryLabel = async (userId, quarter, label) => {
    try {
      await api.put("/commission/recovery-label", { user_id: userId, fiscal_year: fy, quarter, label });
      await reloadRun();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const setCarry = async (userId, quarter, amount) => {
    try {
      await api.put("/commission/carry-override", { fee_earner_id: userId, fiscal_year: fy, quarter, amount });
      await reloadRun();
      toast.success(amount == null ? "Carry-in reset to auto" : "Carry-in overridden");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const panelProps = (q) => ({ q, fy, lock: lockOf(q), items: itemsFor(q), startMonth, isAdmin, onLock: lockQuarter, onUnlock: unlockQuarter, onSetBasic: setBasic, onSetBanking: setBanking, canEditRow, cfg, onReload: reloadRun, onSetCarry: setCarry, onSetRecoveryLabel: setRecoveryLabel });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-semibold tracking-tighter">Commission Engine</h1>
          <p className="text-sm text-muted-foreground mt-1">Per-month banking totals feed the quarterly tiered calculation.</p>
        </div>
        <div className="flex items-center gap-3 border border-border px-4 py-2" data-testid="debtor-toggle-wrap">
          <span className="text-xs uppercase tracking-wide text-muted-foreground">Debtors 90+ rule</span>
          <span className="text-xs font-mono w-7 text-center">{applyDebtor ? "Yes" : "No"}</span>
          <Switch checked={applyDebtor} onCheckedChange={setApplyDebtor} data-testid="debtor-toggle" />
        </div>
      </div>

      <FilterBar filters={filters} setFilters={setFilters} showPeriod={false} />

      {cfg && (
        <div className="border border-border bg-secondary/40 p-4 flex flex-wrap gap-x-6 gap-y-2 text-xs" data-testid="config-summary">
          <span className="flex items-center gap-1.5 text-muted-foreground"><Info className="h-3.5 w-3.5" /> Thresholds:</span>
          <span className="flex items-center gap-1.5 text-muted-foreground"><Info className="h-3.5 w-3.5" /> Rules:</span>
          <span>Break-even <b className="font-mono">{cfg.breakeven_multiple}×</b> basic <span className="text-muted-foreground">+ junior staff</span></span>
          <span>Min <b className="font-mono">{cfg.min_multiple}×</b></span>
          <span>Standard <b className="font-mono">{cfg.breakeven_multiple}–{cfg.top_min}×</b> @ {(cfg.standard_rate * 100).toFixed(0)}%</span>
          <span>Top <b className="font-mono">&gt;{cfg.top_min}×</b> @ {(cfg.top_rate * 100).toFixed(0)}%</span>
          <span>Cap <b className="font-mono">{cfg.cap_multiple}×</b> qtr basic</span>
        </div>
      )}

      <Tabs defaultValue="current">
        <TabsList className="rounded-none">
          <TabsTrigger value="current" className="rounded-none gap-2" data-testid="tab-current"><CalendarClock className="h-4 w-4" /> Current Quarter</TabsTrigger>
          <TabsTrigger value="previous" className="rounded-none gap-2" data-testid="tab-previous"><History className="h-4 w-4" /> Previous Quarters</TabsTrigger>
          <TabsTrigger value="whatif" className="rounded-none gap-2" data-testid="tab-whatif"><FlaskConical className="h-4 w-4" /> What-if</TabsTrigger>
        </TabsList>

        <TabsContent value="current" className="mt-4">
          <QuarterPanel {...panelProps(currentQ)} />
        </TabsContent>

        <TabsContent value="previous" className="mt-4 space-y-4">
          <div className="flex flex-wrap items-end gap-3 border border-border bg-card p-4">
            <div className="space-y-1">
              <div className="text-[10px] uppercase tracking-[0.1em] text-muted-foreground font-semibold">Quarter</div>
              <Select value={prevQuarter} onValueChange={setPrevQuarter}>
                <SelectTrigger className="w-[140px] rounded-none h-9" data-testid="prev-quarter"><SelectValue /></SelectTrigger>
                <SelectContent>{[1, 2, 3, 4].map((q) => <SelectItem key={q} value={String(q)}>Q{q}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <p className="text-xs text-muted-foreground max-w-md">Completed quarters are archived here. Use the <b>Fiscal Year</b> filter above to view a historical year, then pick and lock the quarter.</p>
          </div>
          <QuarterPanel {...panelProps(pq)} />
        </TabsContent>

        <TabsContent value="whatif" className="mt-4">
          {run && cur ? (
            <ScenarioLab key={fy} fy={fy} startMonth={startMonth} actualsRows={rows} actualConfig={cfg} />
          ) : (
            <div className="text-sm text-muted-foreground py-10 text-center">Loading actuals…</div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
